import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useViewportContext } from 'components/spreadsheet/contexts/data/useViewportContext'
import {
  getRowHeightVariable,
  isTreeGroupRow,
  isGroupRowCreate,
  isCollapsedGroupRow
} from 'components/spreadsheet/helpers/functions'
import { HEADER_HEIGHT, SMALL_ROW_HEIGHT, ROW_NUMBER_COLUMN_WIDTH } from 'style/constants'

// Count rows that use SMALL_ROW_HEIGHT (group headers, collapsed group rows, tree group rows, etc.)
// within [0, endIndex] so row bounds can be adjusted for mixed row heights.
function countShortRows(expandedRows: readonly unknown[], endIndex: number): number {
  let count = 0
  const limit = Math.min(endIndex, expandedRows.length - 1)
  for (let i = 0; i <= limit; i++) {
    const r = expandedRows[i]
    if (isGroupRowCreate(r) || isCollapsedGroupRow(r) || isTreeGroupRow(r)) count++
  }
  return count
}

export interface RangeOverlayInput {
  minColPos: number
  maxColPos: number
  minRowIndex: number
  maxRowIndex: number
}

export interface RangeOverlayLayout {
  // Vertical bounds in content-space coordinates (already adjusted for ratio scroll / short rows).
  top: number
  height: number
  // Horizontal bounds of the full range in content-space.
  startLeft: number
  endRight: number
  // Content-space right edge of the leading frozen run. 0 when the range has no frozen columns.
  frozenRight: number
  // True when the maxColPos column itself is frozen — used by fill-handle to decide whether to
  // pin its dot via sticky positioning.
  endColFrozen: boolean
  hasFrozen: boolean
  hasNonFrozen: boolean
  // Ready-to-render rectangles. The non-frozen rect starts at the frozen boundary when the
  // range spans both regions so its left border sits against the frozen overlay's right edge
  // (no doubled line, no gap). Frozen rect gets all 4 borders only when the whole range is
  // frozen; otherwise it drops its right border so the two rectangles join seamlessly.
  nonFrozen: { left: number; width: number } | null
  frozen: { startLeft: number; width: number; allBorders: boolean } | null
}

/**
 * Single source of truth for positioning sticky/frozen overlays over the spreadsheet grid
 * (selection range border, fill handle, fill preview). Given a rectangular column+row range,
 * returns the overlay's bounding box plus a frozen/non-frozen split. Callers render the frozen
 * portion inside a full-scroll-width absolute wrapper with `position: sticky` (handled on the
 * compositor — no horizontal rubber-banding) and the non-frozen portion as a content-scrolling
 * absolute rect that slides under the frozen cells.
 *
 * Tree-leaf column (when present) is treated as a frozen column at position 0.
 *
 * Pass `null` to skip layout computation (for the case where the caller hasn't resolved a valid
 * range yet) — the hook is always called so React's hook rules hold.
 */
export function useRangeOverlayLayout(input: RangeOverlayInput | null): RangeOverlayLayout | null {
  const { allColumns, treeLeafColumn, treeColumnWidth, expandedRowsWithGroups, spreadsheetData } = useDataContext()
  const { rowTopOffset } = useViewportContext()

  if (!input) return null
  const { minColPos, maxColPos, minRowIndex, maxRowIndex } = input

  const rowHeight = getRowHeightVariable(spreadsheetData.viewDetails.rowHeight)
  const nonGroupedColumns = allColumns.filter((c) => !c.isGrouped)
  const treeLeafOffset = treeLeafColumn ? 1 : 0

  const isPosFrozen = (pos: number): boolean => {
    if (treeLeafColumn && pos === 0) return true
    const col = nonGroupedColumns[pos - treeLeafOffset]
    return !!col?.isFrozen
  }
  const leftOfPos = (pos: number): number => {
    if (treeLeafColumn && pos === 0) return ROW_NUMBER_COLUMN_WIDTH
    const col = nonGroupedColumns[pos - treeLeafOffset]
    return col ? col.left : -1
  }
  const rightOfPos = (pos: number): number => {
    if (treeLeafColumn && pos === 0) return ROW_NUMBER_COLUMN_WIDTH + treeColumnWidth
    const col = nonGroupedColumns[pos - treeLeafOffset]
    return col ? col.left + col.column.width : -1
  }

  const startLeft = leftOfPos(minColPos)
  const endRight = rightOfPos(maxColPos)
  if (startLeft < 0 || endRight < 0) return null

  // Frozen columns are always at the left of the table, so the frozen portion of the range is
  // the leading consecutive run of frozen positions starting at minColPos.
  let lastFrozenInRange = -1
  for (let p = minColPos; p <= maxColPos; p++) {
    if (isPosFrozen(p)) lastFrozenInRange = p
    else break
  }

  const hasFrozen = lastFrozenInRange >= minColPos
  const hasNonFrozen = lastFrozenInRange < maxColPos
  const frozenRight = hasFrozen ? rightOfPos(lastFrozenInRange) : 0
  const endColFrozen = isPosFrozen(maxColPos)

  const shortRowsBefore =
    expandedRowsWithGroups.length > 0 ? countShortRows(expandedRowsWithGroups, minRowIndex - 1) : 0
  const shortRowsThrough = expandedRowsWithGroups.length > 0 ? countShortRows(expandedRowsWithGroups, maxRowIndex) : 0
  const shortRowsInRange = shortRowsThrough - shortRowsBefore
  const topOffset = shortRowsBefore * (rowHeight - SMALL_ROW_HEIGHT)
  const heightAdjust = shortRowsInRange * (rowHeight - SMALL_ROW_HEIGHT)

  const top = HEADER_HEIGHT + minRowIndex * rowHeight - topOffset + rowTopOffset
  const height = (maxRowIndex - minRowIndex + 1) * rowHeight - heightAdjust

  return {
    top,
    height,
    startLeft,
    endRight,
    frozenRight,
    endColFrozen,
    hasFrozen,
    hasNonFrozen,
    nonFrozen: hasNonFrozen
      ? {
          left: hasFrozen ? frozenRight : startLeft,
          width: endRight - (hasFrozen ? frozenRight : startLeft)
        }
      : null,
    frozen: hasFrozen
      ? {
          startLeft,
          width: frozenRight - startLeft,
          allBorders: !hasNonFrozen
        }
      : null
  }
}

export interface ColumnPositionResolver {
  /** Returns the position (tree-leaf-aware) of a column public id, or undefined if not found. */
  getPosition: (columnId: string) => number | undefined
}

/**
 * Resolves column public ids into positions within the overlay coordinate space: when the
 * tree-leaf column is present it occupies position 0 and data columns start at position 1.
 * Use this to translate `selectedCell.columnId` / `selectedCellRange.endColumnId` into the
 * `minColPos` / `maxColPos` expected by `useRangeOverlayLayout`.
 */
export function useColumnPositionResolver(): ColumnPositionResolver {
  const { allColumns, treeLeafColumn } = useDataContext()
  const nonGroupedColumns = allColumns.filter((c) => !c.isGrouped)
  const treeLeafOffset = treeLeafColumn ? 1 : 0
  const map = new Map(nonGroupedColumns.map((c, i) => [c.column.publicId, i + treeLeafOffset]))
  if (treeLeafColumn) map.set(treeLeafColumn.column.publicId, 0)
  return { getPosition: (id) => map.get(id) }
}
